import ConfirmDialog from '@components/Shared/Dialog/ConfirmDialog';
import { dialogLine, dialogTray, formDialogTextStyles } from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { type SsoAccountChange } from '@store/api/ssoEndpoints';
import { type TeamRole } from '@store/api/teamEndpoints';
import { orange, red } from '@theme/primitives';
import { type ReactNode } from 'react';

interface Props {
  open: boolean;
  title: string;
  /** What the dialog asks about, as the header's mark. */
  icon?: ReactNode;
  confirmLabel: string;
  /**
   * The button's label when the action signs accounts out, which says so: "Save and sign out 15
   * accounts" rather than "Save". `confirmLabel` otherwise.
   */
  signOutLabel?: (count: number) => string;
  /** What the action does in general, above what it does to each account. */
  description?: ReactNode;
  /** `undefined` while the preview is being worked out. */
  changes: SsoAccountChange[] | undefined;
  /** The other accounts the preview looked at, which stay as they are. */
  unchanged?: number;
  /** Lines the caller adds under the accounts, about the settings as a whole. */
  notes?: string[];
  onClose: () => void;
  onConfirm: () => Promise<boolean | void> | boolean | void;
}

/**
 * Says, before it is done, what handing accounts to single sign-on or saving its rules does to
 * each account: its role, the teams it joins and leaves, whether it is signed out, refused, or
 * placed only at its next sign-in. Accounts nothing happens to are not listed. A line on top
 * counts each kind of change, and the accounts refused come first, then those signed out.
 */
const SsoChangeDialog = ({
  open, title, icon, confirmLabel, signOutLabel, description, changes, unchanged = 0, notes = [], onClose, onConfirm,
}: Props) => {
  const { t } = useLocalization();

  const roleName = (role: string) => t(`User_Role_${role.capitalize()}`);
  const teamRoleName = (role: TeamRole) => t(`Team_Role_${role.toLowerCase().capitalize()}`);

  const lines = (change: SsoAccountChange): string[] => [
    ...(change.governance === 'starts' ? [t('User_SsoChange_Starts')] : []),
    ...(change.governance === 'ends' ? [t('User_SsoChange_Ends')] : []),
    ...(change.governance === 'none' ? [t('User_SsoChange_Inactive')] : []),
    ...(change.role.from !== change.role.to
      ? [t('User_SsoChange_Role', [roleName(change.role.from), roleName(change.role.to)])]
      : []),
    ...change.teams.map((team) => team.from === null
      ? t('User_SsoChange_Joins', [team.name, teamRoleName(team.to!)])
      : team.to === null
        ? t('User_SsoChange_Leaves', [team.name])
        : t('User_SsoChange_TeamRole', [team.name, teamRoleName(team.from), teamRoleName(team.to)])),
    ...(change.passwordLoginEnds ? [t('User_SsoChange_PasswordEnds')] : []),
  ];

  const rank = (change: SsoAccountChange) => (change.refused ? 0 : change.pending ? 1 : 2);
  const affected = (changes ?? [])
    .filter((change) => lines(change).length > 0 || change.refused || change.pending)
    .sort((a, b) => rank(a) - rank(b));

  const count = (test: (change: SsoAccountChange) => boolean) => affected.filter(test).length;
  const refused = count((change) => change.refused);
  const pending = count((change) => change.pending);
  // One plural entry per kind: a sentence of several counts does not inflect in one entry.
  const kinds: [string, number][] = [
    ['User_SsoChange_Summary_Refused', refused],
    ['User_SsoChange_Summary_Pending', pending],
    ['User_SsoChange_Summary_Role', count((change) => change.role.from !== change.role.to)],
    ['User_SsoChange_Summary_Teams', count((change) => change.teams.length > 0)],
    ['User_SsoChange_Summary_PasswordEnds', count((change) => change.passwordLoginEnds)],
  ];
  const summary = kinds.filter(([, n]) => n > 0).map(([key, n]) => t(key, { count: n }));
  const signedOut = refused + pending;

  return (
    <ConfirmDialog
      open={open}
      title={title}
      icon={icon}
      confirmLabel={signOutLabel && signedOut > 0 ? signOutLabel(signedOut) : confirmLabel}
      confirmColor={refused > 0 ? 'error' : 'warning'}
      confirmDisabled={changes === undefined}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <Stack spacing={1.5}>
        {description && <Typography sx={formDialogTextStyles}>{description}</Typography>}

        {changes === undefined && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
        )}

        {changes !== undefined && affected.length === 0 && (
          <Typography sx={{ ...formDialogTextStyles, color: 'text.primary' }}>{t('User_SsoChange_None')}</Typography>
        )}

        {summary.length > 0 && (
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{summary.join(' · ')}</Typography>
        )}
        {affected.length > 0 && unchanged > 0 && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('User_SsoChange_Unchanged', { count: unchanged })}</Typography>
        )}

        {affected.length > 0 && (
          <Stack spacing={1} sx={listStyles}>
            {affected.map((change) => (
              <Box key={change.userId} sx={rowStyles}>
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
                  {change.name}{' '}
                  <Typography component="span" color="text.secondary" sx={{ fontSize: 13 }}>@{change.username}</Typography>
                </Typography>
                {change.refused && (
                  <Typography sx={{ fontSize: 13, color: red[300] }}>{t('User_SsoChange_Refused')}</Typography>
                )}
                {change.pending && (
                  <Typography sx={{ fontSize: 13, color: orange[300] }}>{t('User_SsoChange_Pending')}</Typography>
                )}
                {lines(change).map((line) => (
                  <Typography key={line} color="text.secondary" sx={{ fontSize: 13 }}>{line}</Typography>
                ))}
              </Box>
            ))}
          </Stack>
        )}

        {notes.map((note) => <Alert key={note} severity="warning">{note}</Alert>)}
      </Stack>
    </ConfirmDialog>
  );
};

// The accounts in a well a shade darker than the dialog, as its footer is.
const listStyles = {
  maxHeight: 320,
  overflowY: 'auto',
  border: '1px solid',
  borderColor: dialogLine,
  borderRadius: '8px',
  backgroundColor: dialogTray,
  p: 1.25,
};

const rowStyles = {
  '&:not(:last-of-type)': {
    pb: 1,
    borderBottom: '1px solid',
    borderColor: dialogLine,
  },
};

export default SsoChangeDialog;
